<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useI18n, LOCALES, type Locale } from '@/i18n'
import { useAuthStore } from '@/stores/auth'
import { api } from '@/api'
import { ElMessageBox, ElMessage } from 'element-plus'

const { t, locale, setLocale, locales } = useI18n()
const router = useRouter()
const route = useRoute()
const authStore = useAuthStore()

const user = computed(() => authStore.user)
const isDirector = computed(() => authStore.isDirector)
const subscriptionStatus = computed(() => authStore.subscriptionStatus)
const hasStripeAccount = computed(() => authStore.hasStripeAccount)
const loading = ref(false)
const saved = ref(false)
const loadingCancel = ref(false)
const loadingReactivate = ref(false)
const loadingPortal = ref(false)
const loadingBillingPortal = ref(false)

const form = ref({
  display_name: '',
  locale: 'fr' as Locale,
  theme_pref: 'dark'
})

const transactions = ref<any[]>([])
const loadingTransactions = ref(false)

const subscriptionEndDate = computed(() => {
  if (!user.value?.director_subscription_end) return null
  return new Date(user.value.director_subscription_end).toLocaleDateString('fr-FR', {
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  })
})

async function loadTransactions() {
  loadingTransactions.value = true
  try {
    const response = await api.get('/users/me/transactions')
    transactions.value = response.data.items
  } catch (error) {
    console.error('Failed to load transactions:', error)
  } finally {
    loadingTransactions.value = false
  }
}

async function saveProfile() {
  loading.value = true
  saved.value = false
  
  const success = await authStore.updateProfile({
    display_name: form.value.display_name,
    locale: form.value.locale,
    theme_pref: form.value.theme_pref
  })
  
  if (success) {
    saved.value = true
    setTimeout(() => saved.value = false, 3000)
  }
  
  loading.value = false
}

async function cancelSubscription() {
  try {
    await ElMessageBox.confirm(
      t('profile.subscription.cancel_confirm'),
      t('profile.subscription.cancel_title'),
      {
        confirmButtonText: t('profile.subscription.confirm_cancel'),
        cancelButtonText: t('common.cancel'),
        type: 'warning'
      }
    )
    
    loadingCancel.value = true
    const success = await authStore.cancelSubscription()
    
    if (success) {
      ElMessage.success(t('profile.subscription.cancelled'))
    } else {
      ElMessage.error(t('profile.subscription.cancel_error'))
    }
  } catch {
    // User cancelled the dialog
  } finally {
    loadingCancel.value = false
  }
}

async function reactivateSubscription() {
  loadingReactivate.value = true
  try {
    const success = await authStore.reactivateSubscription()
    
    if (success) {
      ElMessage.success(t('profile.subscription.reactivated'))
    } else {
      ElMessage.error(t('profile.subscription.reactivate_error'))
    }
  } finally {
    loadingReactivate.value = false
  }
}

async function openStripePortal() {
  loadingPortal.value = true
  try {
    const url = await authStore.getSubscriptionPortalUrl()
    if (url) {
      window.location.href = url
    } else {
      ElMessage.error(t('profile.subscription.portal_error'))
    }
  } finally {
    loadingPortal.value = false
  }
}

async function openBillingPortal() {
  loadingBillingPortal.value = true
  try {
    const url = await authStore.getSubscriptionPortalUrl()
    if (url) {
      window.location.href = url
    } else {
      ElMessage.error(t('profile.billing.portal_error'))
    }
  } finally {
    loadingBillingPortal.value = false
  }
}

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('fr-FR', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })
}

onMounted(async () => {
  // Check if returning from successful subscription
  if (route.query.subscription === 'success') {
    ElMessage.success(t('profile.subscription_success'))
    // Refresh user data to get updated subscription status
    await authStore.fetchUser()
    // Clean URL
    router.replace({ path: '/profile', query: {} })
  }
  
  if (user.value) {
    form.value = {
      display_name: user.value.display_name || '',
      locale: user.value.locale as Locale,
      theme_pref: user.value.theme_pref
    }
  }
  loadTransactions()
})
</script>

<template>
  <div class="profile-view">
    <div class="profile-header">
      <h1>{{ t('profile.title') }}</h1>
    </div>

    <div class="profile-content">
      <!-- Director Subscription -->
      <div class="profile-section subscription-section">
        <h2>{{ t('profile.subscription.title') }}</h2>
        
        <div v-if="isDirector" class="subscription-active">
          <div class="subscription-badge">
            <span class="badge-icon">🎬</span>
            <span class="badge-text">Director</span>
          </div>
          
          <div class="subscription-info">
            <p v-if="subscriptionStatus === 'active'" class="status-active">
              ✓ {{ t('profile.subscription.active') }}
            </p>
            <p v-else-if="subscriptionStatus === 'canceling'" class="status-canceling">
              ⚠️ {{ t('profile.subscription.canceling') }}
            </p>
            
            <p v-if="subscriptionEndDate" class="renewal-date">
              {{ subscriptionStatus === 'canceling' 
                ? t('profile.subscription.ends_on', { date: subscriptionEndDate })
                : t('profile.subscription.renews_on', { date: subscriptionEndDate })
              }}
            </p>
          </div>
          
          <div class="subscription-actions">
            <el-button
              v-if="subscriptionStatus === 'canceling'"
              type="success"
              :loading="loadingReactivate"
              @click="reactivateSubscription"
            >
              {{ t('profile.subscription.reactivate') }}
            </el-button>
            
            <el-button
              v-else
              type="danger"
              plain
              :loading="loadingCancel"
              @click="cancelSubscription"
            >
              {{ t('profile.subscription.cancel') }}
            </el-button>
            
            <el-button
              :loading="loadingPortal"
              @click="openStripePortal"
            >
              {{ t('profile.subscription.manage_payment') }}
            </el-button>
          </div>
        </div>
        
        <div v-else class="subscription-inactive">
          <p>{{ t('profile.subscription.not_subscribed') }}</p>
          <router-link to="/pricing" class="subscribe-link">
            {{ t('profile.subscription.subscribe_now') }}
          </router-link>
        </div>
      </div>

      <!-- Profile Form -->
      <div class="profile-section">
        <h2>{{ t('profile.section_info') }}</h2>
        
        <el-form :model="form" label-position="top" class="profile-form">
          <el-form-item label="Email">
            <el-input :model-value="user?.email" disabled />
          </el-form-item>
          
          <el-form-item :label="t('profile.display_name')">
            <el-input v-model="form.display_name" />
          </el-form-item>
          
          <el-form-item :label="t('profile.language')">
            <el-select v-model="form.locale" class="full-width">
              <el-option 
                v-for="(info, code) in locales" 
                :key="code"
                :label="`${info.flag} ${info.name}`"
                :value="code"
              />
            </el-select>
          </el-form-item>
          
          <el-form-item :label="t('profile.theme')">
            <el-radio-group v-model="form.theme_pref">
              <el-radio-button value="dark">
                🌙 {{ t('profile.theme.dark') }}
              </el-radio-button>
              <el-radio-button value="light">
                ☀️ {{ t('profile.theme.light') }}
              </el-radio-button>
            </el-radio-group>
          </el-form-item>
          
          <el-form-item>
            <el-button 
              type="primary" 
              :loading="loading"
              @click="saveProfile"
            >
              {{ t('profile.save') }}
            </el-button>
            <el-tag v-if="saved" type="success" class="saved-tag">
              ✓ {{ t('profile.saved') }}
            </el-tag>
          </el-form-item>
        </el-form>

        <!-- Stripe Billing Portal -->
        <div v-if="hasStripeAccount" class="billing-section">
          <div class="billing-divider"></div>
          <div class="billing-content">
            <div class="billing-info">
              <h3>{{ t('profile.billing.title') }}</h3>
              <p>{{ t('profile.billing.description') }}</p>
            </div>
            <el-button 
              :loading="loadingBillingPortal"
              @click="openBillingPortal"
            >
              <span class="billing-icon">💳</span>
              {{ t('profile.billing.access_portal') }}
            </el-button>
          </div>
        </div>
      </div>

      <!-- Transactions -->
      <div class="profile-section">
        <h2>{{ t('profile.transactions') }}</h2>
        
        <div v-if="loadingTransactions" class="transactions-loading">
          <el-skeleton :rows="3" animated />
        </div>
        
        <div v-else-if="transactions.length === 0" class="transactions-empty">
          <p>{{ t('profile.no_transactions') }}</p>
        </div>
        
        <div v-else class="transactions-list">
          <div 
            v-for="tx in transactions" 
            :key="tx.id"
            class="transaction-item"
          >
            <div class="tx-icon">
              <span v-if="tx.type === 'purchase'">💰</span>
              <span v-else-if="tx.type === 'use'">🎬</span>
              <span v-else-if="tx.type === 'bonus'">🎁</span>
              <span v-else>💎</span>
            </div>
            <div class="tx-details">
              <span class="tx-description">{{ tx.description || tx.type }}</span>
              <span class="tx-date">{{ formatDate(tx.created_at) }}</span>
            </div>
            <div class="tx-amount" :class="{ positive: tx.credits_delta > 0 }">
              {{ tx.credits_delta > 0 ? '+' : '' }}{{ tx.credits_delta }}
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.profile-view {
  min-height: calc(100vh - 64px);
  padding: var(--m2v-space-8) var(--m2v-space-6);
  max-width: 800px;
  margin: 0 auto;
}

.profile-header h1 {
  font-family: var(--m2v-font-display);
  font-size: 2rem;
  margin-bottom: var(--m2v-space-8);
}

.profile-section {
  background: var(--m2v-bg-surface);
  border: 1px solid var(--m2v-border-subtle);
  border-radius: var(--m2v-radius-xl);
  padding: var(--m2v-space-6);
  margin-bottom: var(--m2v-space-6);
}

.profile-section h2 {
  font-size: 1.25rem;
  margin-bottom: var(--m2v-space-6);
}

/* Subscription Section */
.subscription-section {
  border-color: #f59e0b;
}

.subscription-active {
  text-align: center;
}

.subscription-badge {
  display: inline-flex;
  align-items: center;
  gap: var(--m2v-space-2);
  padding: var(--m2v-space-3) var(--m2v-space-5);
  background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
  border-radius: var(--m2v-radius-full);
  margin-bottom: var(--m2v-space-4);
}

.badge-icon {
  font-size: 1.5rem;
}

.badge-text {
  color: white;
  font-weight: 700;
  font-size: 1.25rem;
}

.subscription-info {
  margin-bottom: var(--m2v-space-6);
}

.status-active {
  color: #10b981;
  font-weight: 600;
  margin-bottom: var(--m2v-space-2);
}

.status-canceling {
  color: var(--m2v-warning);
  font-weight: 600;
  margin-bottom: var(--m2v-space-2);
}

.renewal-date {
  color: var(--m2v-text-muted);
  font-size: 0.875rem;
}

.subscription-actions {
  display: flex;
  gap: var(--m2v-space-3);
  justify-content: center;
  flex-wrap: wrap;
}

.subscription-inactive {
  text-align: center;
  padding: var(--m2v-space-6);
}

.subscription-inactive p {
  color: var(--m2v-text-muted);
  margin-bottom: var(--m2v-space-4);
}

.subscribe-link {
  display: inline-block;
  padding: var(--m2v-space-3) var(--m2v-space-6);
  background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
  color: white;
  font-weight: 600;
  border-radius: var(--m2v-radius-lg);
  text-decoration: none;
  transition: all var(--m2v-transition-fast);
}

.subscribe-link:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(245, 158, 11, 0.3);
}

/* Profile Form */
.profile-form {
  max-width: 400px;
}

.full-width {
  width: 100%;
}

.saved-tag {
  margin-left: var(--m2v-space-3);
}

/* Billing Section */
.billing-section {
  margin-top: var(--m2v-space-6);
}

.billing-divider {
  height: 1px;
  background: var(--m2v-border-subtle);
  margin-bottom: var(--m2v-space-6);
}

.billing-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: var(--m2v-space-4);
}

.billing-info h3 {
  font-size: 1rem;
  font-weight: 600;
  margin: 0 0 var(--m2v-space-1) 0;
}

.billing-info p {
  font-size: 0.875rem;
  color: var(--m2v-text-muted);
  margin: 0;
}

.billing-icon {
  margin-right: var(--m2v-space-2);
}

/* Transactions */
.transactions-loading,
.transactions-empty {
  padding: var(--m2v-space-6);
  text-align: center;
  color: var(--m2v-text-muted);
}

.transactions-list {
  display: flex;
  flex-direction: column;
  gap: var(--m2v-space-3);
}

.transaction-item {
  display: flex;
  align-items: center;
  gap: var(--m2v-space-4);
  padding: var(--m2v-space-4);
  background: var(--m2v-bg-elevated);
  border-radius: var(--m2v-radius-md);
}

.tx-icon {
  font-size: 1.5rem;
}

.tx-details {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.tx-description {
  font-weight: 500;
}

.tx-date {
  font-size: 0.75rem;
  color: var(--m2v-text-muted);
}

.tx-amount {
  font-weight: 700;
  font-size: 1.125rem;
  color: var(--m2v-error);
}

.tx-amount.positive {
  color: var(--m2v-success);
}
</style>
